import * as pc from "playcanvas";
import { MATCH_FORMAT } from "@hupper/shared";
import type { GameClient, MatchHudState, RemotePlayerState } from "../net/GameClient";
import type { FirstPersonController } from "../player/FirstPersonController";
import type { CharacterFactory } from "../player/CharacterFactory";
import { RemotePlayer } from "../player/RemotePlayer";
import { VsIntroOverlay, type VsSideInfo } from "../ui/VsIntroOverlay";

/**
 * VsIntro — the one-time, pre-buy "VS" cinematic.
 *
 * When the server enters the `intro` phase (right after an opponent is found,
 * before the first buy), this stages the two fighters on the mid platform —
 * the orange Terrorist holding an AK-47 and the blue Counter-Terrorist with an
 * M4A1-S — frames them with a dedicated camera, and shows the {@link
 * VsIntroOverlay} (photos + names + a "VS" + a 10s countdown). The random side
 * draw is decided server-side; this just reveals it. Everything tears down
 * when the phase leaves `intro` and the buy phase begins.
 *
 * The fighters are plain {@link RemotePlayer}s so the rifle sits correctly in
 * the hand via the exact same grip + hand-bone tracking the opponent body
 * uses — no bespoke posing code.
 */

const CT_ACCENT = new pc.Color(0.32, 0.45, 0.72); // blue
const T_ACCENT = new pc.Color(0.74, 0.42, 0.22); // orange
const SKY = new pc.Color(0.52, 0.72, 0.90); // matches the gameplay camera clear

/** The mid platform is a 12×12 block spanning y 0..1.5 → fighters stand on
 *  its top face. */
const STAGE_Y = 1.5;
/** Half the gap between the two fighters (metres), centred on the platform. */
const STAGE_X = 1.4;

/** Gameplay HUD layers to hide while the cinematic is up (crosshair, the
 *  health/ammo block, weapon list, scoreboard timer, kill feed) — they'd
 *  otherwise bleed through the transparent VS overlay. Scoped to a body class
 *  so removing it restores each element's own display. */
const HUD_HIDE_CSS =
  "body.vs-intro-active #crosshair," +
  "body.vs-intro-active #scoreboard," +
  "body.vs-intro-active #hud-center," +
  "body.vs-intro-active #weapon-inv," +
  "body.vs-intro-active #kill-feed{display:none!important;}";

export class VsIntro {
  private readonly app: pc.AppBase;
  private readonly client: GameClient | null;
  private readonly player: FirstPersonController;
  private readonly factory: CharacterFactory;
  private readonly overlay = new VsIntroOverlay();

  private active = false;
  private lastPhase: string | null = null;
  private fighters: RemotePlayer[] = [];
  private camera: pc.Entity | null = null;
  private elapsed = 0;

  constructor(
    app: pc.AppBase,
    client: GameClient | null,
    player: FirstPersonController,
    factory: CharacterFactory,
  ) {
    this.app = app;
    this.client = client;
    this.player = player;
    this.factory = factory;
    this.app.on("update", (dt: number) => this.update(dt));
    this.client?.onMatchHud((hud) => this.onHud(hud));

    const style = document.createElement("style");
    style.textContent = HUD_HIDE_CSS;
    document.head.appendChild(style);
  }

  private onHud(hud: MatchHudState): void {
    if (hud.phase === "intro" && this.lastPhase !== "intro") this.begin();
    else if (hud.phase !== "intro" && this.active) this.end();
    if (this.active && typeof hud.introSecondsLeft === "number") {
      this.overlay.setCountdown(hud.introSecondsLeft);
    }
    this.lastPhase = hud.phase;
  }

  private begin(override?: { ct: VsSideInfo; t: VsSideInfo }): void {
    if (this.active) return;
    this.active = true;
    this.elapsed = 0;

    const { ct, t } = override ?? this.readSides();
    this.overlay.show(ct, t);
    document.body.classList.add("vs-intro-active");

    // Hand the screen to a dedicated cinematic camera; the first-person one
    // (and its view-model child) stops rendering until the intro ends.
    const playerCam = this.player.getCameraEntity();
    if (playerCam.camera) playerCam.camera.enabled = false;

    const cam = new pc.Entity("vs-intro-camera");
    cam.addComponent("camera", { clearColor: SKY, fov: 50, nearClip: 0.05, farClip: 200 });
    this.app.root.addChild(cam);
    this.camera = cam;
    this.placeCamera(0);

    // Camera sits on −Z, so a fighter at yaw 0 (model faces −Z) looks into it.
    // CT lands on frame-left (+X from this camera), T on frame-right (−X) —
    // matching the overlay cards.
    this.fighters.push(this.makeFighter("vsintro-ct", "ct", "m4a1_s", +STAGE_X));
    this.fighters.push(this.makeFighter("vsintro-t", "t", "ak47", -STAGE_X));
  }

  /** Map the two cached player snapshots onto their drawn sides. */
  private readSides(): { ct: VsSideInfo; t: VsSideInfo } {
    let ct: VsSideInfo = { name: "Counter-Terrorist" };
    let t: VsSideInfo = { name: "Terrorist" };
    const selfId = this.client?.sessionId;
    this.client?.forEachKnownPlayer((s) => {
      const info: VsSideInfo = {
        name: s.name || "Player",
        avatarUrl: s.avatarUrl || undefined,
        you: !!selfId && s.sessionId === selfId,
      };
      if (s.team === "t") t = info;
      else ct = info;
    });
    return { ct, t };
  }

  private makeFighter(id: string, team: "ct" | "t", weaponId: string, x: number): RemotePlayer {
    const state: RemotePlayerState = {
      sessionId: id,
      name: id,
      x,
      y: STAGE_Y,
      z: 0,
      yaw: 0,
      pitch: 0,
      health: 100,
      weaponId,
      lastProcessedInputSeq: 0,
      team,
    };
    return new RemotePlayer(this.app, id, state, team === "ct" ? CT_ACCENT : T_ACCENT, this.factory);
  }

  /** Gentle push-in over the intro (t: 0 → 1). No flashing — just a slow dolly. */
  private placeCamera(t: number): void {
    if (!this.camera) return;
    const z = -7.2 + 1.4 * t;
    const y = 2.7 - 0.15 * t;
    this.camera.setPosition(0, y, z);
    this.camera.lookAt(0, 2.2, 0);
  }

  private update(dt: number): void {
    if (!this.active) return;
    this.elapsed += dt;
    for (const f of this.fighters) f.update(dt);
    this.placeCamera(Math.max(0, Math.min(1, this.elapsed / MATCH_FORMAT.introSeconds)));
  }

  private end(): void {
    if (!this.active) return;
    this.active = false;
    this.overlay.hide();
    document.body.classList.remove("vs-intro-active");
    for (const f of this.fighters) f.destroy();
    this.fighters = [];
    if (this.camera) {
      this.camera.destroy();
      this.camera = null;
    }
    const playerCam = this.player.getCameraEntity();
    if (playerCam.camera) playerCam.camera.enabled = true;
  }

  /** Standalone visual preview (`?vsintro=1`) — runs the cinematic without a
   *  live match so the framing / poses can be checked in one browser tab. It
   *  loops so the scene stays up while the character models finish loading. */
  debugStart(): void {
    const run = () => {
      this.begin({ ct: { name: "Nova", you: true }, t: { name: "Kzinho" } });
      let s = MATCH_FORMAT.introSeconds;
      this.overlay.setCountdown(s);
      const iv = window.setInterval(() => {
        s -= 1;
        this.overlay.setCountdown(Math.max(0, s));
        if (s <= 0) {
          window.clearInterval(iv);
          this.end();
          window.setTimeout(run, 1000);
        }
      }, 1000);
    };
    run();
  }
}
